import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, TrendingUp, Target, Zap, ArrowRight, Shield, Lock, Award, Users, BarChart2, Plus } from "lucide-react";
import StickyCTABar from "@/components/StickyCTABar";
import CountdownTimer from "@/components/CountdownTimer";
import ExitIntentModal from "@/components/ExitIntentModal";
import { captureUTMParams } from "@/utils/utmUtils";
import "@/styles/landing.css";

export default function NewOfferPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [offerExpired, setOfferExpired] = useState(false);
  const [faqOpen, setFaqOpen] = useState(0);

  // Capture UTM params on mount
  useEffect(() => { captureUTMParams(); }, []);

  const startAnalysis = () => navigate("/intake");

  const stats = [
    { value: "15,000+", label: "Resumes Analyzed" },
    { value: "3.5x", label: "More Interview Calls" },
    { value: "92%", label: "ATS Pass Rate After Fix" },
    { value: "4 min", label: "Average Analysis Time" }
  ];

  const features = [
    {
      title: "ATS Readiness Score",
      description: "Most resumes never reach a human recruiter. They're filtered out by Applicant Tracking Software (ATS) before anyone reads them. We scan your resume for format issues, keyword gaps, and parser errors — then give you a score out of 100.",
      example: 'Identifies hidden ATS blockers like graphics, tables, and missing keywords your target role requires',
      badge: "Most Important",
      icon: <Target className="w-6 h-6" />
    },
    {
      title: "Blocker Fix Engine",
      description: "Don't just know what's wrong — fix it. Get exact, copy-paste-ready recommendations for every ATS blocker in your resume. Rewrite bullets with the right keywords. Restructure sections the way ATS expects.",
      example: 'Turns "managed team projects" → "led cross-functional team of 8, delivering ₹12L project on time"',
      badge: null,
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Career Velocity Simulator",
      description: "Not sure whether to switch companies, industries, or roles? See how different career moves affect your salary trajectory over 12, 24, and 36 months — using real Indian market data from 10,000+ placements.",
      example: 'Simulates: Current Role (₹8L) → Target Role (₹16L) — here\'s the fastest path',
      badge: "High Impact",
      icon: <TrendingUp className="w-6 h-6" />
    },
    {
      title: "Smart Job Matching",
      description: "Stop applying to jobs at random. We match you to roles where your specific mix of skills has the highest callback probability — and tell you exactly why each role is or isn't a good fit.",
      example: '"88% match: Senior Analyst at Flipkart — here\'s what makes you a fit"',
      badge: null,
      icon: <Award className="w-6 h-6" />
    },
    {
      title: "Personalized Career Roadmap",
      description: "A 30/60/90-day action plan built for your exact career situation. Know what to fix first, which skills to add, and exactly which roles to target — so you stop wasting time and start getting results.",
      example: '"Week 1: Fix resume. Week 2-3: Apply to 12 high-fit roles. Week 4: Interview prep."',
      badge: "High Impact",
      icon: <BarChart2 className="w-6 h-6" />
    },
    {
      title: "Market Trend Insights",
      description: "Know which roles are surging and which are saturated — before you invest months in the wrong direction. Get salary benchmarks, in-demand skills, and hiring trends specific to your target industry.",
      example: '"Product roles up 38% YoY in Bangalore — here are the top 5 hiring companies"',
      badge: null,
      icon: <Users className="w-6 h-6" />
    }
  ];

  const testimonials = [
    {
      name: "Ananya Singh",
      img: "/avatars/ananya_singh.jpg",
      role: "Software Engineer → Senior Engineer, Hyderabad",
      company: "Placed at Swiggy — ₹22 LPA",
      initials: "AS",
      color: "bg-[#2832C2]",
      rating: 5,
      text: "I had been applying for 4 months with zero callbacks. PlacementOS showed me I had 11 ATS blockers — including a two-column layout that was completely breaking my resume. Fixed everything in 2 hours and got 6 calls in the next 10 days. Honestly life-changing."
    },
    {
      name: "Karthik Rajan",
      img: "/avatars/karthik_rajan.jpg",
      role: "Marketing Manager → Growth Lead, Chennai",
      company: "Salary went from ₹9L → ₹18L in 5 months",
      initials: "KR",
      color: "bg-[#FF6B35]",
      rating: 5,
      text: "The career velocity simulator showed me I was undervaluing myself by almost 40%. That data gave me the confidence to negotiate harder and hold out for the right role. Got an offer that was ₹9L above my previous salary. Best ₹99 I've ever spent."
    },
    {
      name: "Divya Nair",
      img: "/avatars/divya_nair.jpg",
      role: "Business Analyst → Product Manager, Bangalore",
      company: "3 offers in 3 weeks — accepted ₹24 LPA",
      initials: "DN",
      color: "bg-[#16a34a]",
      rating: 5,
      text: "I thought my resume was fine. PlacementOS gave it a 31/100. I was horrified — but also grateful. The AI rewrote my resume with proper keywords and structure, and the job matching found roles I didn't even know to look for. Three offers in three weeks after months of silence."
    }
  ];

  const faqs = [
    {
      question: "What exactly do I get for ₹99?",
      answer: "You get a complete career analysis: your ATS readiness score out of 100, a detailed list of every blocker in your resume with exact fix recommendations, a personalized 30/60/90-day career roadmap, smart job matches with fit scores, and a career velocity simulation. The free analysis shows your ATS score and top blockers — you only pay if you want the full, detailed report."
    },
    {
      question: "Is this useful for my industry / role?",
      answer: "Yes. Our AI analyzes resumes across all industries and functions — tech, finance, marketing, operations, sales, HR, and more. The keyword database and role-matching engine covers 500+ job categories across India. Whether you're a fresher or have 15 years of experience, the system tailors recommendations to your specific situation."
    },
    {
      question: "How is this different from Naukri or LinkedIn?",
      answer: "Naukri and LinkedIn are job listing platforms — they show you jobs but don't tell you why you're not getting callbacks. PlacementOS diagnoses the root cause (usually ATS compatibility issues), fixes it, and then matches you to roles where your specific skill set has the highest probability of getting a response. It's the strategy layer on top of job boards."
    },
    {
      question: "How long does the analysis take?",
      answer: "The initial free analysis takes about 4 minutes. You upload your resume, our AI scans it for ATS compatibility, and you get your score and top blockers immediately. The full detailed report (paid) is available instantly after payment — no waiting."
    },
    {
      question: "Will my data be safe?",
      answer: "Absolutely. Your resume and analysis data are encrypted and never shared with third parties. You can delete your data at any time. We use Stripe for secure payment processing, which is PCI DSS Level 1 compliant — the same standard used by major banks."
    },
    {
      question: "I'm a fresher. Will this work for me?",
      answer: "Yes — freshers often benefit the most because they have the most ATS issues (wrong formatting, missing keywords, weak bullet points). The career roadmap feature is especially valuable for freshers as it shows exactly which skills to build and roles to target in your first 1-3 years."
    }
  ];

  return (
    <div className="ap-root min-h-screen">
      <ExitIntentModal lpSlug="newoffer" />

      {/* Hero Section */}
      <section id="hero" ref={heroRef} className="ap-mesh ap-grid-overlay relative pt-24 pb-10" style={{ overflowX: "clip" }}>
        <div className="ap-section max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="ap-eyebrow ap-rise ap-rise-1 mb-6">AI-Powered Resume & Career Intelligence</p>
              <h1 className="ap-display ap-rise ap-rise-2 font-extrabold tracking-tight text-[clamp(30px,5vw,52px)] leading-[1.06] mb-6 text-[var(--ap-ink)]">
                Your Skills Are Real.<br />
                Your Resume Just Isn't <span className="ap-grad-text italic">Showing It.</span>
              </h1>
              <p className="ap-rise ap-rise-3 text-xl text-[var(--ap-muted)] mb-8 leading-relaxed">
                98% of Fortune 500 companies use ATS software that auto-rejects resumes before any human sees them. PlacementOS scans your resume the same way — finds every blocker — and fixes it in 4 minutes.
              </p>
              <div className="ap-rise ap-rise-4 mt-6">
                <button onClick={startAnalysis} className="ap-btn ap-btn-fire ap-btn-lg" data-testid="newoffer-hero-cta">
                  Start My Free Analysis
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="relative ap-rise ap-rise-2">
              {/* floating 3D chips */}
              <div className="hidden md:flex ap-glass ap-float absolute -left-6 -top-5 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
                <TrendingUp className="w-5 h-5 text-[var(--ap-green)]" />
                <div><div className="ap-display font-extrabold text-[var(--ap-ink)] leading-none">3.5x</div><div className="text-[11px] text-[var(--ap-muted)]">More Interview Calls</div></div>
              </div>
              <div className="hidden md:flex ap-glass ap-float-2 absolute -right-6 -bottom-6 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
                <span className="w-8 h-8 rounded-full grid place-items-center text-white text-xs font-bold shrink-0" style={{ background: "var(--ap-fire)" }}>92</span>
                <div><div className="text-[12px] font-bold text-[var(--ap-ink)] leading-none">92% pass rate</div><div className="text-[11px] text-[var(--ap-muted)]">ATS after fix</div></div>
              </div>

              <div className="ap-card ap-tilt overflow-hidden p-0 relative z-10 rounded-[26px]">
                <div className="px-4 py-3 flex items-center gap-2 border-b border-[var(--ap-line)] bg-[var(--ap-canvas)]">
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 bg-red-400 rounded-full"></span>
                    <span className="w-3 h-3 bg-yellow-400 rounded-full"></span>
                    <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                  </div>
                  <span className="text-xs text-[var(--ap-muted)] ml-2">placementos.ai/ats-analysis</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-20 h-20 rounded-full border-4 border-red-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-red-500">28</span>
                    </div>
                    <div>
                      <h4 className="ap-display font-semibold text-[var(--ap-ink)]">ATS Readiness Score</h4>
                      <p className="text-sm text-red-500">High rejection risk — 4 critical blockers found</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-red-500 mt-0.5">✕</span>
                      <span className="text-[var(--ap-ink-2)]">Two-column layout — parsers read it as blank space</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-red-500 mt-0.5">✕</span>
                      <span className="text-[var(--ap-ink-2)]">Missing the keywords your target role requires</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-amber-500 mt-0.5">!</span>
                      <span className="text-[var(--ap-ink-2)]">"Managed team projects" — no metrics, no scope</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-[var(--ap-green)] mt-0.5">✓</span>
                      <span className="text-[var(--ap-ink-2)]">Fix available — estimated new score: 94</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[var(--ap-card)] border-b border-[var(--ap-line)] py-12">
        <div className="ap-section max-w-6xl grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, idx) => (
            <div key={idx} className="text-center">
              <div className="ap-display text-3xl md:text-4xl font-extrabold text-[var(--ap-ink)] mb-2">{stat.value}</div>
              <div className="text-sm text-[var(--ap-muted)]">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">The Real Problem</p>
          <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-6">
            You're not underqualified.<br />
            You're <span className="ap-grad-text italic">invisible.</span>
          </h2>
          <p className="text-lg text-[var(--ap-ink-2)] leading-relaxed mb-12">
            You've delivered results. You've built skills. But if your resume can't pass an ATS scan, none of that matters. Most resumes get silently rejected — not because of what you've done, but because of how it's written. We find the exact issues and fix them.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="ap-card p-8 text-center">
              <div className="ap-display text-5xl font-extrabold text-red-500 mb-2">75%</div>
              <p className="text-sm text-[var(--ap-ink-2)]">of qualified candidates are rejected by ATS before a human ever reads their resume</p>
            </div>
            <div className="ap-card p-8 text-center">
              <div className="ap-display text-5xl font-extrabold text-[var(--ap-violet)] mb-2">6 mo</div>
              <p className="text-sm text-[var(--ap-ink-2)]">average time job seekers in India spend searching without a systematic approach</p>
            </div>
            <div className="ap-card p-8 text-center">
              <div className="ap-display text-5xl font-extrabold text-[var(--ap-orange)] mb-2">68%</div>
              <p className="text-sm text-[var(--ap-ink-2)]">of job seekers don't know their resume has ATS compatibility issues</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[var(--ap-canvas)] py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">The PlacementOS Way</p>
            <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-4">
              Fix first. Then apply<br />
              where you'll actually win.
            </h2>
            <p className="text-lg text-[var(--ap-ink-2)] max-w-3xl mx-auto">
              PlacementOS is an AI-powered career operating system. It diagnoses exactly why you're not getting callbacks, fixes your resume for ATS compatibility, and shows you the precise roles where your profile has the highest chance of success.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="ap-card ap-pop p-8 text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4 mx-auto text-white" style={{ background: "var(--ap-fire)" }}>1</div>
              <h3 className="ap-display text-2xl font-bold text-[var(--ap-ink)] mb-3">Upload Your Resume</h3>
              <p className="text-[var(--ap-muted)]">Drop your resume in any format. Our AI reads it the way a recruiter's ATS system would — and instantly finds what's stopping you from getting interviews.</p>
            </div>
            <div className="ap-card ap-pop p-8 text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4 mx-auto text-white" style={{ background: "var(--ap-fire)" }}>2</div>
              <h3 className="ap-display text-2xl font-bold text-[var(--ap-ink)] mb-3">Get Your AI Diagnosis</h3>
              <p className="text-[var(--ap-muted)]">See your ATS readiness score (out of 100), every blocker killing your applications, and a prioritized fix list — tailored to your target role and industry.</p>
            </div>
            <div className="ap-card ap-pop p-8 text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4 mx-auto text-white" style={{ background: "var(--ap-fire)" }}>3</div>
              <h3 className="ap-display text-2xl font-bold text-[var(--ap-ink)] mb-3">Fix, Match & Apply Smart</h3>
              <p className="text-[var(--ap-muted)]">Apply the AI-generated fixes, unlock job matches ranked by fit score, and follow a 30/60/90-day roadmap that takes you from invisible to shortlisted.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Everything You Need</p>
            <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-4">
              Stop guessing.<br />
              Start getting hired.
            </h2>
            <p className="text-lg text-[var(--ap-ink-2)] max-w-3xl mx-auto">
              Every feature is built around one goal: getting you more interviews. No fluff, no generic advice — just data-driven tools that work.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="ap-card ap-pop p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--ap-violet-soft)" }}>
                    <span className="text-[var(--ap-violet)]">{feature.icon}</span>
                  </div>
                  {feature.badge && (
                    <span className="ap-chip" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>
                      {feature.badge}
                    </span>
                  )}
                </div>
                <h3 className="ap-display text-2xl font-bold text-[var(--ap-ink)] mb-3">{feature.title}</h3>
                <p className="text-[var(--ap-ink-2)] mb-4 leading-relaxed">{feature.description}</p>
                <div className="bg-[var(--ap-canvas)] border-l-4 p-4 rounded" style={{ borderColor: "var(--ap-violet)" }}>
                  <p className="text-sm text-[var(--ap-muted)] italic">{feature.example}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10 bg-[var(--ap-canvas)]">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Real Results</p>
            <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-4">
              Professionals who stopped<br />
              guessing and started winning.
            </h2>
            <p className="text-lg text-[var(--ap-ink-2)]">
              Real career outcomes from real people across India's job market.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="ap-card ap-pop p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`relative w-12 h-12 ${testimonial.color} text-white rounded-full flex items-center justify-center font-bold overflow-hidden shrink-0`}>
                    {testimonial.initials}
                    {testimonial.img && <img src={testimonial.img} alt={testimonial.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => e.currentTarget.remove()} />}
                  </div>
                  <div className="flex-1">
                    <div className="ap-display font-semibold text-[var(--ap-ink)]">{testimonial.name}</div>
                    <div className="text-xs text-[var(--ap-muted)]">{testimonial.role}</div>
                  </div>
                </div>
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-[var(--ap-orange)]">★</span>
                  ))}
                </div>
                <p className="text-sm text-[var(--ap-ink-2)] mb-4 leading-relaxed">{testimonial.text}</p>
                <div className="text-sm font-semibold text-[var(--ap-violet)]">{testimonial.company}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Comparison */}
      <section className="py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">The Transformation</p>
            <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-4">
              From invisible to shortlisted.
            </h2>
            <p className="text-xl text-[var(--ap-muted)]">
              Here's what changes when you stop guessing and start using a system.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="ap-card p-8" style={{ borderColor: "rgba(239,68,68,.35)" }}>
              <h3 className="ap-display text-2xl font-bold mb-6 text-red-500">Before PlacementOS</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="text-red-500 text-xl">×</span>
                  <span className="text-[var(--ap-ink-2)]">Applied to 100+ jobs, got almost zero callbacks</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 text-xl">×</span>
                  <span className="text-[var(--ap-ink-2)]">Resume looked polished but scored 28/100 on ATS</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 text-xl">×</span>
                  <span className="text-[var(--ap-ink-2)]">No idea which roles actually matched your profile</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 text-xl">×</span>
                  <span className="text-[var(--ap-ink-2)]">Underpaid and unsure how to negotiate a raise</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 text-xl">×</span>
                  <span className="text-[var(--ap-ink-2)]">Refreshing Naukri and LinkedIn every few hours</span>
                </li>
              </ul>
            </div>

            <div className="ap-card p-8" style={{ borderColor: "rgba(22,163,74,.35)" }}>
              <h3 className="ap-display text-2xl font-bold mb-6 text-[var(--ap-green)]">After PlacementOS</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                  <span className="text-[var(--ap-ink-2)]">Applied to 15 matched roles, got 8 interview calls</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                  <span className="text-[var(--ap-ink-2)]">ATS score jumped from 28 → 94 after AI fixes</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                  <span className="text-[var(--ap-ink-2)]">Smart matching surfaced 3 dream roles you'd missed</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                  <span className="text-[var(--ap-ink-2)]">Clear roadmap to double salary in under 12 months</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                  <span className="text-[var(--ap-ink-2)]">Landed new role — ₹12L+ above previous CTC</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-[var(--ap-canvas)] py-10">
        <div className="ap-section max-w-4xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Launch Pricing</p>
            <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-4">
              Everything you need to get placed.
            </h2>
            <p className="text-lg text-[var(--ap-ink-2)]">
              One plan. No subscriptions. Pay once, own your career analysis forever.
            </p>
            <div className="mt-4">
              <CountdownTimer onExpired={() => setOfferExpired(true)} />
            </div>
          </div>

          <div className="ap-card relative overflow-hidden p-8 md:p-12" style={{ borderColor: "var(--ap-violet)", borderWidth: 2 }}>
            <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: "var(--ap-fire)" }} />
            <div className="text-center mb-8">
              <span className="ap-chip mb-4" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>
                {offerExpired ? "Price Updated" : "Launch Offer — Limited Time"}
              </span>
              <h3 className="ap-display text-3xl font-bold text-[var(--ap-ink)] mb-2">Complete Career Analysis</h3>
              <p className="text-[var(--ap-muted)]">Full ATS diagnosis + career roadmap + smart matching</p>
            </div>

            <div className="space-y-3 mb-8">
              <div className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                <span className="text-[var(--ap-ink-2)]">ATS Readiness Score & Full Analysis</span>
                <span className="text-[var(--ap-muted)] line-through">₹499</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                <span className="text-[var(--ap-ink-2)]">ATS Blocker Diagnosis + Fix Plan</span>
                <span className="text-[var(--ap-muted)] line-through">₹399</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                <span className="text-[var(--ap-ink-2)]">Personalized Career Roadmap (30/60/90)</span>
                <span className="text-[var(--ap-muted)] line-through">₹599</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                <span className="text-[var(--ap-ink-2)]">Smart Job Matching + Relevancy Scores</span>
                <span className="text-[var(--ap-muted)] line-through">₹299</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                <span className="text-[var(--ap-ink-2)]">Career Velocity Simulation</span>
                <span className="text-[var(--ap-muted)] line-through">₹99</span>
              </div>
              <div className="flex items-center justify-between py-3 font-semibold">
                <span className="text-[var(--ap-muted)]">Total Value</span>
                <span className="text-[var(--ap-muted)] line-through">₹1,895</span>
              </div>
            </div>

            <div className="text-center mb-8">
              <div className="text-[var(--ap-muted)] mb-2">You pay today</div>
              <div className="ap-display text-6xl font-extrabold text-[var(--ap-ink)]">
                {offerExpired ? (
                  <><span className="text-3xl">₹</span>499<p className="text-base text-[var(--ap-muted)] mt-2 font-normal">Offer expired</p></>
                ) : (
                  <><span className="text-3xl">₹</span>99<p className="text-[var(--ap-muted)] mt-2 text-base font-normal">One-time payment · Lifetime access</p></>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <button
                onClick={startAnalysis}
                className="ap-btn ap-btn-fire ap-btn-lg w-full"
                data-testid="newoffer-pricing-cta"
              >
                Start My Free Analysis
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <p className="text-center text-sm text-[var(--ap-muted)]">
              Free analysis first — pay only if you want the full report
            </p>

            <div className="flex justify-center gap-6 mt-6 text-sm text-[var(--ap-muted)]">
              <span className="flex items-center gap-1"><Lock className="w-4 h-4" /> Secure</span>
              <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> SSL</span>
              <span className="flex items-center gap-1"><Zap className="w-4 h-4" /> Instant</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10">
        <div className="ap-section max-w-4xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Got Questions?</p>
            <h2 className="ap-display ap-h2 text-[var(--ap-ink)]">Frequently asked questions.</h2>
          </div>

          <div className="mt-12 space-y-5">
            {faqs.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div key={i} className="ap-card overflow-hidden transition-shadow"
                  style={open ? { borderColor: "var(--ap-violet)", boxShadow: "0 18px 50px -24px rgba(91,61,245,.4)" } : {}}>
                  <button onClick={() => setFaqOpen(open ? -1 : i)} className="group w-full flex items-center justify-between gap-4 text-left px-6 py-6 bg-transparent border-0 cursor-pointer">
                    <span className="ap-display font-bold text-[16.5px] text-[var(--ap-ink)] group-hover:text-[var(--ap-violet)] transition-colors">{f.question}</span>
                    <span className="shrink-0 w-8 h-8 rounded-full grid place-items-center transition-colors"
                      style={{ background: open ? "var(--ap-violet)" : "var(--ap-violet-soft)", color: open ? "#fff" : "var(--ap-violet)" }}>
                      <Plus className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
                    </span>
                  </button>
                  <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden"><p className="px-6 pb-6 text-[15px] text-[var(--ap-muted)] leading-relaxed">{f.answer}</p></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="ap-mesh ap-grid-overlay relative py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">Ready?</p>
          <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-6">
            Your next role is waiting.<br />
            Your resume just isn't <span className="ap-grad-text italic">reaching</span> it.
          </h2>
          <p className="text-xl text-[var(--ap-muted)] mb-8 leading-relaxed">
            In 4 minutes, you'll know exactly what's blocking your applications — and exactly how to fix it. The analysis is free. The transformation starts now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={startAnalysis}
              className="ap-btn ap-btn-fire ap-btn-lg"
            >
              Start My Free Analysis
              <ArrowRight className="w-5 h-5" />
            </button>
            <a href="#features" className="ap-btn ap-btn-ghost ap-btn-lg">
              See All Features
            </a>
          </div>
        </div>
      </section>

      {/* Sticky CTA Bar */}
      <StickyCTABar heroRef={heroRef} onCTAClick={startAnalysis} />
    </div>
  );
}
